import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import api from "@/lib/api";

const SWAN_PHOTO = "/api/htt/assets/swan-platinum-photo.jpg";

export default function Join() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const ref = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("ref") : "") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.has_access) navigate("/dashboard");
  }, [user, navigate]);

  const startCheckout = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(email, password, name, ref);
      await startCheckout();
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
      setBusy(false);
    }
  };

  const alreadyAccount = !!user && !user.has_access;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block velvet">
        <img src={SWAN_PHOTO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-deep via-purple-deep/75 to-purple/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/api/htt/assets/mark.svg" alt="" className="w-9 h-9 rounded-lg ring-1 ring-gold/40" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold-pale">Oxytocin-Learning</span>
          </Link>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-4">Founding cohort · £39</p>
            <h2 className="font-heading text-white text-4xl leading-tight max-w-md text-balance">
              Eight modules. Three movements. Around three hours.
            </h2>
            <ul className="mt-8 space-y-3 text-white/80 text-[15px]">
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-gold-pale" /> Every workbook & download included</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-gold-pale" /> Your CPD certificate on completion</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-gold-pale" /> A standing invitation to the community</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-cream px-6 py-16">
        <div className="w-full max-w-sm">
          {alreadyAccount ? (
            <>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-3">One step away</p>
              <h1 className="font-heading text-purple text-4xl mb-2">Hello{user.name ? `, ${user.name}` : ""}.</h1>
              <p className="text-lav text-[15px] mb-8">
                Your account is ready. Complete your enrolment to unlock all eight modules.
              </p>
              {err && <p className="text-[13px] text-red-700 bg-red-50 rounded-lg px-3 py-2 mb-4">{err}</p>}
              <button
                onClick={startCheckout}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-purple-deep font-bold rounded-full py-3.5 hover:bg-gold-pale transition-colors disabled:opacity-60"
                data-testid="join-pay-btn"
              >
                {busy ? "Opening secure checkout…" : "Pay £39 & unlock the course"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-[12px] text-lav flex items-center gap-2 justify-center">
                <Lock className="w-3.5 h-3.5" /> Secured by Stripe
              </p>
            </>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-3">Join the founding cohort</p>
              <h1 className="font-heading text-purple text-4xl mb-2">Create your account</h1>
              {ref && (
                <p className="mb-3 inline-flex items-center gap-2 text-[12px] font-semibold text-gold-dark bg-gold/10 border border-gold/30 rounded-full px-3 py-1.5" data-testid="join-referral-badge">
                  <ShieldCheck className="w-3.5 h-3.5" /> A colleague invited you to travel the path
                </p>
              )}
              <p className="text-lav text-[15px] mb-8">£39 today, rising to £99. You'll go straight to secure checkout.</p>

              <form onSubmit={submitRegister} className="space-y-4" data-testid="join-form">
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.14em] text-lav mb-1.5">First name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="What should we call you?"
                    className="w-full rounded-xl border border-line bg-white px-4 py-3 text-ink placeholder:text-lav/50 focus:outline-none focus:ring-2 focus:ring-gold"
                    data-testid="join-name"
                  />
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.14em] text-lav mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-gold"
                    data-testid="join-email"
                  />
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.14em] text-lav mb-1.5">Password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full rounded-xl border border-line bg-white px-4 py-3 text-ink placeholder:text-lav/50 focus:outline-none focus:ring-2 focus:ring-gold"
                    data-testid="join-password"
                  />
                </div>
                {err && (
                  <p className="text-[13px] text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="join-error">
                    {err}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-purple-deep text-cream font-bold rounded-full py-3.5 hover:bg-purple transition-colors disabled:opacity-60"
                  data-testid="join-submit"
                >
                  {busy ? "One moment…" : "Continue to secure checkout"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>

              <p className="mt-7 text-[14px] text-lav">
                Already enrolled?{" "}
                <Link to="/login" className="text-purple-mid font-semibold hover:text-purple underline underline-offset-4" data-testid="join-to-login">
                  Sign in →
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
